import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Box,Button,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,IconButton,InputLabel,MenuItem,Select,Stack,Switch,TextField,Typography } from "@mui/material";
import { useEffect,useState } from "react";

const WEEKDAYS=[
  {value:1,label:"Lunes"},
  {value:2,label:"Martes"},
  {value:3,label:"Miércoles"},
  {value:4,label:"Jueves"},
  {value:5,label:"Viernes"},
  {value:6,label:"Sábado"},
  {value:7,label:"Domingo"}
];

const EMPTY_RANGE={
  weekday:1,
  entry_time:"08:00",
  exit_time:"17:00",
  lunch_enabled:false,
  lunch_start:"12:00",
  lunch_end:"13:00",
  break_minutes:0,
  crosses_midnight:false,
  is_active:true
};

const EMPTY_SCHEDULE={
  id:null,
  name:"",
  valid_from:"",
  valid_until:"",
  is_active:true,
  ranges:[{...EMPTY_RANGE}]
};

const EMPTY_ASSIGNMENT={
  id:null,
  person:"",
  schedule:"",
  valid_from:"",
  valid_until:"",
  is_active:true
};

function getApiMessage(data,fallback){
  if(typeof data?.message==="string")return data.message;
  if(typeof data?.detail==="string")return data.detail;
  if(data?.errors&&typeof data.errors==="object")return Object.values(data.errors).flat().join(" ");
  return fallback;
}

function normalizeTime(value,fallback){
  if(!value)return fallback;
  return String(value).slice(0,5);
}

export default function AttendanceModals({fetchUser,schedules,people,scheduleModal,assignmentModal,onCloseSchedule,onCloseAssignment,onSaved,onError}){
  const [scheduleForm,setScheduleForm]=useState(EMPTY_SCHEDULE);
  const [assignmentForm,setAssignmentForm]=useState(EMPTY_ASSIGNMENT);
  const [savingSchedule,setSavingSchedule]=useState(false);
  const [savingAssignment,setSavingAssignment]=useState(false);

  useEffect(()=>{
    const schedule=scheduleModal.schedule;
    if(!scheduleModal.open){
      setScheduleForm(EMPTY_SCHEDULE);
      return;
    }
    if(!schedule){
      setScheduleForm({...EMPTY_SCHEDULE,ranges:[{...EMPTY_RANGE}]});
      return;
    }
    setScheduleForm({
      id:schedule.id,
      name:schedule.name||"",
      valid_from:schedule.valid_from||"",
      valid_until:schedule.valid_until||"",
      is_active:schedule.is_active===true,
      ranges:Array.isArray(schedule.ranges)&&schedule.ranges.length?schedule.ranges.map((range)=>({
        weekday:Number(range.weekday),
        entry_time:normalizeTime(range.entry_time,"08:00"),
        exit_time:normalizeTime(range.exit_time,"17:00"),
        lunch_enabled:Boolean(range.lunch_start&&range.lunch_end),
        lunch_start:normalizeTime(range.lunch_start,"12:00"),
        lunch_end:normalizeTime(range.lunch_end,"13:00"),
        break_minutes:Number(range.break_minutes||0),
        crosses_midnight:range.crosses_midnight===true,
        is_active:range.is_active!==false
      })):[{...EMPTY_RANGE}]
    });
  },[scheduleModal]);

  useEffect(()=>{
    const assignment=assignmentModal.assignment;
    if(!assignmentModal.open){
      setAssignmentForm(EMPTY_ASSIGNMENT);
      return;
    }
    if(!assignment){
      setAssignmentForm({...EMPTY_ASSIGNMENT});
      return;
    }
    setAssignmentForm({
      id:assignment.id,
      person:assignment.person?.id||"",
      schedule:assignment.schedule?.id||"",
      valid_from:assignment.valid_from||"",
      valid_until:assignment.valid_until||"",
      is_active:assignment.is_active!==false
    });
  },[assignmentModal]);

  const readJson=async(response)=>{
    try{return await response.json();}catch{return {};}
  };

  const addRange=()=>{
    const usedDays=scheduleForm.ranges.map((range)=>Number(range.weekday));
    const available=WEEKDAYS.find((day)=>!usedDays.includes(day.value));
    if(!available){
      onError("Ya añadiste los siete días de la semana.");
      return;
    }
    setScheduleForm((previous)=>({...previous,ranges:[...previous.ranges,{...EMPTY_RANGE,weekday:available.value}]}));
  };

  const updateRange=(index,field,value)=>{
    setScheduleForm((previous)=>({...previous,ranges:previous.ranges.map((range,rangeIndex)=>rangeIndex===index?{...range,[field]:value}:range)}));
  };

  const removeRange=(index)=>{
    if(scheduleForm.ranges.length===1)return;
    setScheduleForm((previous)=>({...previous,ranges:previous.ranges.filter((_,rangeIndex)=>rangeIndex!==index)}));
  };

  const saveSchedule=async()=>{
    if(!scheduleForm.name.trim()){
      onError("El nombre del horario es obligatorio.");
      return;
    }
    if(!scheduleForm.ranges.length){
      onError("Debes añadir al menos un día.");
      return;
    }
    const weekdays=scheduleForm.ranges.map((range)=>Number(range.weekday));
    if(new Set(weekdays).size!==weekdays.length){
      onError("No puedes repetir el mismo día.");
      return;
    }
    for(const range of scheduleForm.ranges){
      if(!range.entry_time||!range.exit_time){
        onError("Todos los días deben tener hora de entrada y salida.");
        return;
      }
      if(range.lunch_enabled&&(!range.lunch_start||!range.lunch_end)){
        onError("Debes ingresar el inicio y el fin del lunch.");
        return;
      }
    }
    setSavingSchedule(true);
    try{
      const editing=Boolean(scheduleForm.id);
      const response=await fetchUser(editing?`/attendance/api/schedules/${scheduleForm.id}/`:"/attendance/api/schedules/",{
        method:editing?"PUT":"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          name:scheduleForm.name.trim(),
          valid_from:scheduleForm.valid_from||null,
          valid_until:scheduleForm.valid_until||null,
          is_active:scheduleForm.is_active,
          ranges:scheduleForm.ranges.map((range)=>({
            weekday:Number(range.weekday),
            entry_time:range.entry_time,
            exit_time:range.exit_time,
            lunch_start:range.lunch_enabled?range.lunch_start:null,
            lunch_end:range.lunch_enabled?range.lunch_end:null,
            break_minutes:range.lunch_enabled?0:Number(range.break_minutes||0),
            crosses_midnight:range.crosses_midnight,
            is_active:range.is_active
          }))
        })
      });
      const data=await readJson(response);
      if(!response.ok||data.ok!==true)throw new Error(getApiMessage(data,editing?"No se pudo actualizar el horario.":"No se pudo crear el horario."));
      onCloseSchedule();
      await onSaved(data.message||(editing?"Horario actualizado correctamente.":"Horario creado correctamente."));
    }catch(error){
      onError(error instanceof Error?error.message:"No se pudo guardar el horario.");
    }finally{
      setSavingSchedule(false);
    }
  };

  const saveAssignment=async()=>{
    if(!assignmentForm.person){
      onError("Debes seleccionar un usuario.");
      return;
    }
    if(!assignmentForm.schedule){
      onError("Debes seleccionar un horario.");
      return;
    }
    setSavingAssignment(true);
    try{
      const editing=Boolean(assignmentForm.id);
      const response=await fetchUser(editing?`/attendance/api/assignments/${assignmentForm.id}/`:"/attendance/api/assignments/",{
        method:editing?"PUT":"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          person:Number(assignmentForm.person),
          schedule:Number(assignmentForm.schedule),
          valid_from:assignmentForm.valid_from||null,
          valid_until:assignmentForm.valid_until||null,
          is_active:assignmentForm.is_active
        })
      });
      const data=await readJson(response);
      if(!response.ok||data.ok!==true)throw new Error(getApiMessage(data,editing?"No se pudo actualizar la asignación.":"No se pudo asignar el horario."));
      onCloseAssignment();
      await onSaved(data.message||(editing?"Asignación actualizada correctamente.":"Horario asignado correctamente."));
    }catch(error){
      onError(error instanceof Error?error.message:"No se pudo guardar la asignación.");
    }finally{
      setSavingAssignment(false);
    }
  };

  return (
    <>
      <Dialog open={scheduleModal.open} onClose={savingSchedule?undefined:onCloseSchedule} fullWidth maxWidth="md">
        <DialogTitle>{scheduleForm.id?"Editar horario de asistencia":"Nuevo horario de asistencia"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{paddingTop:1}}>
            <TextField fullWidth required label="Nombre del horario" placeholder="Ejemplo: Técnicos de lunes a viernes" value={scheduleForm.name} onChange={(event)=>setScheduleForm((previous)=>({...previous,name:event.target.value}))} />
            <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},gap:2}}>
              <TextField fullWidth type="date" label="Válido desde" value={scheduleForm.valid_from} onChange={(event)=>setScheduleForm((previous)=>({...previous,valid_from:event.target.value}))} InputLabelProps={{shrink:true}} />
              <TextField fullWidth type="date" label="Válido hasta" value={scheduleForm.valid_until} onChange={(event)=>setScheduleForm((previous)=>({...previous,valid_until:event.target.value}))} InputLabelProps={{shrink:true}} />
            </Box>
            <Box sx={{minHeight:52,display:"flex",alignItems:"center",justifyContent:"space-between",paddingX:1.5,border:"1px solid #e5e7eb",borderRadius:2}}>
              <Typography sx={{fontSize:14,fontWeight:700}}>Horario activo</Typography>
              <Switch checked={scheduleForm.is_active} onChange={(event)=>setScheduleForm((previous)=>({...previous,is_active:event.target.checked}))} />
            </Box>
            <Box>
              <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,marginBottom:1.5}}>
                <Typography sx={{fontSize:18,fontWeight:800}}>Días y horas</Typography>
                <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addRange}>Añadir día</Button>
              </Box>
              <Stack spacing={1.5}>
                {scheduleForm.ranges.map((range,index)=>(
                  <Box key={index} sx={{padding:1.5,border:"1px solid #e5e7eb",borderRadius:2,backgroundColor:"#f9fafb"}}>
                    <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1.3fr repeat(2,minmax(0,1fr)) auto"},alignItems:"center",gap:1.2}}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Día</InputLabel>
                        <Select value={range.weekday} label="Día" onChange={(event)=>updateRange(index,"weekday",Number(event.target.value))}>
                          {WEEKDAYS.map((weekday)=><MenuItem key={weekday.value} value={weekday.value}>{weekday.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField fullWidth size="small" type="time" label="Entrada" value={range.entry_time} onChange={(event)=>updateRange(index,"entry_time",event.target.value)} InputLabelProps={{shrink:true}} />
                      <TextField fullWidth size="small" type="time" label="Salida" value={range.exit_time} onChange={(event)=>updateRange(index,"exit_time",event.target.value)} InputLabelProps={{shrink:true}} />
                      <IconButton color="error" disabled={scheduleForm.ranges.length===1} onClick={()=>removeRange(index)}><DeleteOutlineRoundedIcon /></IconButton>
                    </Box>
                    <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,marginTop:1.5,paddingY:0.5}}>
                      <Typography sx={{fontSize:14,fontWeight:700}}>Incluir lunch</Typography>
                      <Switch checked={range.lunch_enabled} onChange={(event)=>updateRange(index,"lunch_enabled",event.target.checked)} />
                    </Box>
                    {range.lunch_enabled?(
                      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},gap:1.2,marginTop:1}}>
                        <TextField fullWidth size="small" type="time" label="Inicio del lunch" value={range.lunch_start} onChange={(event)=>updateRange(index,"lunch_start",event.target.value)} InputLabelProps={{shrink:true}} />
                        <TextField fullWidth size="small" type="time" label="Fin del lunch" value={range.lunch_end} onChange={(event)=>updateRange(index,"lunch_end",event.target.value)} InputLabelProps={{shrink:true}} />
                      </Box>
                    ):(
                      <TextField fullWidth size="small" type="number" label="Minutos de descanso opcional" value={range.break_minutes} onChange={(event)=>updateRange(index,"break_minutes",Math.max(0,Number(event.target.value||0)))} inputProps={{min:0}} sx={{marginTop:1}} />
                    )}
                    <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,marginTop:1.5}}>
                      <Typography sx={{fontSize:14,fontWeight:700}}>Turno cruza medianoche</Typography>
                      <Switch checked={range.crosses_midnight} onChange={(event)=>updateRange(index,"crosses_midnight",event.target.checked)} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSchedule} disabled={savingSchedule}>Cancelar</Button>
          <Button variant="contained" onClick={saveSchedule} disabled={savingSchedule}>{savingSchedule?<CircularProgress size={20} sx={{color:"#ffffff"}} />:scheduleForm.id?"Guardar cambios":"Crear horario"}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={assignmentModal.open} onClose={savingAssignment?undefined:onCloseAssignment} fullWidth maxWidth="sm">
        <DialogTitle>{assignmentForm.id?"Editar asignación":"Asignar horario de asistencia"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{paddingTop:1}}>
            <FormControl fullWidth>
              <InputLabel>Usuario</InputLabel>
              <Select value={assignmentForm.person} label="Usuario" disabled={Boolean(assignmentForm.id)} onChange={(event)=>setAssignmentForm((previous)=>({...previous,person:event.target.value}))}>
                {people.map((person)=><MenuItem key={person.id} value={person.id}>{person.name} - {person.identification}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Horario</InputLabel>
              <Select value={assignmentForm.schedule} label="Horario" onChange={(event)=>setAssignmentForm((previous)=>({...previous,schedule:event.target.value}))}>
                {schedules.filter((schedule)=>schedule.is_active||schedule.id===Number(assignmentForm.schedule)).map((schedule)=><MenuItem key={schedule.id} value={schedule.id}>{schedule.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,1fr))"},gap:2}}>
              <TextField fullWidth type="date" label="Válido desde" value={assignmentForm.valid_from} onChange={(event)=>setAssignmentForm((previous)=>({...previous,valid_from:event.target.value}))} InputLabelProps={{shrink:true}} />
              <TextField fullWidth type="date" label="Válido hasta" value={assignmentForm.valid_until} onChange={(event)=>setAssignmentForm((previous)=>({...previous,valid_until:event.target.value}))} InputLabelProps={{shrink:true}} />
            </Box>
            {assignmentForm.id&&(
              <Box sx={{minHeight:52,display:"flex",alignItems:"center",justifyContent:"space-between",paddingX:1.5,border:"1px solid #e5e7eb",borderRadius:2}}>
                <Typography sx={{fontSize:14,fontWeight:700}}>Asignación activa</Typography>
                <Switch checked={assignmentForm.is_active} onChange={(event)=>setAssignmentForm((previous)=>({...previous,is_active:event.target.checked}))} />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseAssignment} disabled={savingAssignment}>Cancelar</Button>
          <Button variant="contained" onClick={saveAssignment} disabled={savingAssignment}>{savingAssignment?<CircularProgress size={20} sx={{color:"#ffffff"}} />:assignmentForm.id?"Guardar cambios":"Asignar horario"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}